import { API_URL_V3 } from 'constants/index';
import type { Payment } from 'models/payment';
import { fetchWithAuthentication } from 'utils/fetchJson/fetchJson';

import type {
  CreatePaymentRequestBody,
  DeleteFileRequestParams,
  DeleteFileResponse,
  FetchFileUploadURLRequestParams,
  FetchPaymentRefundsRequestParams,
  FetchProofsRequestParams,
  FetchSmsNotificationRequestParams,
  FetchUserPaymentsRequestParams,
  FetchUserPaymentsResponse,
  GenerateFilenameResponse,
  GenerateFilenameUrlRequestParams,
  PaymentRefundsResponse,
  ProofResponseBody,
  SMSNotificationResponse,
  UpdatePaymentRequestBody,
  UpdateSMSNotificationRequestParams,
  UploadFileToUrlRequestParams,
} from './types';

export interface PaymentsService {
  create: (payload: CreatePaymentRequestBody) => Promise<Payment>;
  fetchUserPayments: (
    payload: FetchUserPaymentsRequestParams,
  ) => Promise<FetchUserPaymentsResponse>;
  fetchSMSNotification: (
    payload: FetchSmsNotificationRequestParams,
  ) => Promise<SMSNotificationResponse>;
  update: (payload: UpdatePaymentRequestBody) => Promise<Payment>;
  updateSMSNotification: (
    // missed
    payload: UpdateSMSNotificationRequestParams,
  ) => Promise<SMSNotificationResponse>;
  deleteFile: (payload: DeleteFileRequestParams) => Promise<DeleteFileResponse>; // missed
  fetchProofs: (
    // missed
    payload: FetchProofsRequestParams,
  ) => Promise<ProofResponseBody>;
  generateProofUrl: (
    // missed
    payload: GenerateFilenameUrlRequestParams,
  ) => Promise<GenerateFilenameResponse>;
  generateDocumentUrl: (
    // missed
    payload: GenerateFilenameUrlRequestParams,
  ) => Promise<GenerateFilenameResponse>;
  fetchFileUploadURL: (
    // missed
    payload: FetchFileUploadURLRequestParams,
  ) => Promise<{ url?: string }>;
  fetchPaymentRefunds: (
    // missed
    payload: FetchPaymentRefundsRequestParams,
  ) => Promise<PaymentRefundsResponse>;
  uploadFileToUrl: (payload: UploadFileToUrlRequestParams) => Promise<void>; // missed
}

export const payments: PaymentsService = {
  create: ({
    orderId,
    token,
    locale = 'EN',
    hideUserManagement = false,
    recurring,
    captchaResponse,
    platform,
    recipient,
    whatsappNotification,
  }) => {
    const queryParams = [
      `token=${token}`,
      `locale=${locale}`,
      ...(hideUserManagement ? ['hideUserManagement=true'] : []),
      ...(recipient === 'PTU' ? ['avoid_maintenance=true'] : []),
    ].join('&');
    const path = `${API_URL_V3}/orders/${orderId}/payments?${queryParams}`;
    const body = JSON.stringify({
      ...{ captcha_response: captchaResponse },
      ...(recurring && { recurring }),
      platform,
      recipient,
      ...(whatsappNotification != null && {
        whatsapp_notification: whatsappNotification,
      }),
    });
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },

  fetchUserPayments: ({ perPage = 5, page = 1 } = {}) => {
    const path = `${API_URL_V3}/payments?per_page=${perPage}&page=${
      page < 1 ? 1 : page
    }`;
    const options = {
      method: 'GET',
    };

    return fetchWithAuthentication(path, options);
  },

  fetchSMSNotification: ({ url }) => {
    const options = { method: 'GET' };

    return fetchWithAuthentication(url, options);
  },

  update: ({ id, token, ...payload }) => {
    const path = `${API_URL_V3}/payments/${id}?token=${token}`;
    const body = JSON.stringify(payload);
    const options = {
      method: 'PATCH',
      body,
    };

    return fetchWithAuthentication(path, options);
  },

  updateSMSNotification: ({ number, active, url }) => {
    let options;

    switch (active) {
      case true:
        const payload = { number };
        const body = JSON.stringify(payload);
        options = { method: 'POST', body };
        break;
      case false:
        options = { method: 'DELETE' };
        break;
    }

    return fetchWithAuthentication(url, options);
  },

  generateDocumentUrl: ({ id, token, filename }) => {
    const path = `${API_URL_V3}/payments/${id}/documents/upload_url?token=${token}`;
    const payload = { filename };
    const body = JSON.stringify(payload);
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },

  fetchFileUploadURL: ({ documentUploadURL }) => {
    const options = { method: 'POST' };

    return fetchWithAuthentication(documentUploadURL, options);
  },

  deleteFile: ({ url }) => {
    const path = url;
    const options = { method: 'DELETE' };

    return fetchWithAuthentication(path, options);
  },

  fetchProofs: ({ id, token }) => {
    const path = `${API_URL_V3}/payments/${id}/proofs?token=${token}`;
    const options = { method: 'GET' };

    return fetchWithAuthentication(path, options);
  },

  generateProofUrl: ({ id, token, filename }) => {
    const path = `${API_URL_V3}/payments/${id}/proofs/upload_url?token=${token}`;
    const payload = { filename };
    const body = JSON.stringify(payload);
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },

  uploadFileToUrl: ({ file, url, info }) => {
    const body = new FormData();
    body.append('file', file);
    body.append('info', JSON.stringify(info));

    const path = url;
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },

  fetchPaymentRefunds: ({ paymentId }) => {
    const path = `${API_URL_V3}/payments/${paymentId}/refunds`;
    const options = { method: 'GET' };

    return fetchWithAuthentication(path, options);
  },
};
